import type React from 'react';
import {useEffect, useState} from 'react';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {LinkPlugin} from '@lexical/react/LexicalLinkPlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {MarkdownShortcutPlugin} from '@lexical/react/LexicalMarkdownShortcutPlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {$convertFromMarkdownString, $convertToMarkdownString, BOLD_ITALIC_STAR, BOLD_STAR, BOLD_UNDERSCORE, ITALIC_STAR,
    ITALIC_UNDERSCORE, LINK, ORDERED_LIST, UNORDERED_LIST} from '@lexical/markdown';
import {$isLinkNode, AutoLinkNode, LinkNode, TOGGLE_LINK_COMMAND} from '@lexical/link';
import {$isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode,
    REMOVE_LIST_COMMAND} from '@lexical/list';
import {
    $getSelection, $isRangeSelection, CAN_REDO_COMMAND, CAN_UNDO_COMMAND, COMMAND_PRIORITY_LOW, FORMAT_TEXT_COMMAND,
    KEY_DOWN_COMMAND, REDO_COMMAND, SELECTION_CHANGE_COMMAND, UNDO_COMMAND,
} from 'lexical';
import {Box, Divider, IconButton, Stack, Tooltip} from '@mui/material';
import {FormatBold, FormatItalic, FormatListBulleted, FormatListNumbered, Link as LinkIcon, Redo, Undo} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

// Only what the toolbar offers: paragraphs, bold, italic, lists and links.
const TRANSFORMERS = [UNORDERED_LIST, ORDERED_LIST, BOLD_ITALIC_STAR, BOLD_STAR, ITALIC_STAR, BOLD_UNDERSCORE,
    ITALIC_UNDERSCORE, LINK];
const NODES = [ListNode, ListItemNode, LinkNode, AutoLinkNode];

const TEXT_SX = {
    typography: 'body2',
    '& p': {m: 0, mb: 1},
    '& p:last-child': {mb: 0},
    '& ul, & ol': {m: 0, mb: 1, pl: 3},
    '& a': {color: 'primary.main'},
} as const;

type Format = {bold: boolean; italic: boolean; list: 'bullet' | 'number' | null; link: boolean; undo: boolean; redo: boolean};

function Toolbar() {
    const [editor] = useLexicalComposerContext();
    const [f, setF] = useState<Format>({bold: false, italic: false, list: null, link: false, undo: false, redo: false});

    useEffect(() => {
        const read = () => editor.getEditorState().read(() => {
            const sel = $getSelection();
            if (!$isRangeSelection(sel)) return;
            const node = sel.anchor.getNode();
            const block = node.getTopLevelElement();
            const list = $isListNode(block) ? (block.getListType() === 'number' ? 'number' : 'bullet') : null;
            const link = $isLinkNode(node) || $isLinkNode(node.getParent());
            setF(prev => ({...prev, bold: sel.hasFormat('bold'), italic: sel.hasFormat('italic'), list, link}));
        });
        const offUpdate = editor.registerUpdateListener(() => read());
        const offSel = editor.registerCommand(SELECTION_CHANGE_COMMAND, () => {
            read();
            return false;
        }, COMMAND_PRIORITY_LOW);
        const offUndo = editor.registerCommand(CAN_UNDO_COMMAND, v => {
            setF(prev => ({...prev, undo: v}));
            return false;
        }, COMMAND_PRIORITY_LOW);
        const offRedo = editor.registerCommand(CAN_REDO_COMMAND, v => {
            setF(prev => ({...prev, redo: v}));
            return false;
        }, COMMAND_PRIORITY_LOW);
        return () => {
            offUpdate();
            offSel();
            offUndo();
            offRedo();
        };
    }, [editor]);

    const link = () => {
        if (f.link) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            return;
        }
        const url = window.prompt('Link address');
        if (url) editor.dispatchCommand(TOGGLE_LINK_COMMAND, /^[a-z]+:/i.test(url) ? url : `https://${url}`);
    };

    const tools: {label: string; icon: React.ReactNode; active?: boolean; disabled?: boolean; run: () => void}[] = [
        {label: 'Bold (Ctrl+B)', icon: <FormatBold fontSize="small"/>, active: f.bold,
            run: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')},
        {label: 'Italic (Ctrl+I)', icon: <FormatItalic fontSize="small"/>, active: f.italic,
            run: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')},
        {label: 'Bulleted list', icon: <FormatListBulleted fontSize="small"/>, active: f.list === 'bullet',
            run: () => editor.dispatchCommand(f.list === 'bullet' ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND, undefined)},
        {label: 'Numbered list', icon: <FormatListNumbered fontSize="small"/>, active: f.list === 'number',
            run: () => editor.dispatchCommand(f.list === 'number' ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND, undefined)},
        {label: f.link ? 'Remove link' : 'Link', icon: <LinkIcon fontSize="small"/>, active: f.link, run: link},
    ];
    const history = [
        {label: 'Undo (Ctrl+Z)', icon: <Undo fontSize="small"/>, disabled: !f.undo, run: () => editor.dispatchCommand(UNDO_COMMAND, undefined)},
        {label: 'Redo (Ctrl+Y)', icon: <Redo fontSize="small"/>, disabled: !f.redo, run: () => editor.dispatchCommand(REDO_COMMAND, undefined)},
    ];
    const button = (t: typeof tools[number]) => (
        <Tooltip key={t.label} title={t.label}>
            <span>
                <IconButton size="small" aria-label={t.label} aria-pressed={t.active} disabled={t.disabled}
                            onMouseDown={e => e.preventDefault()} onClick={t.run}
                            sx={{color: t.active ? 'primary.main' : undefined, bgcolor: t.active ? 'action.selected' : undefined}}>
                    {t.icon}
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Stack direction="row" alignItems="center" gap={0.25} role="toolbar" aria-label="Formatting"
               sx={{px: 0.5, py: 0.25, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap'}}>
            {tools.map(button)}
            <Divider orientation="vertical" flexItem sx={{mx: 0.5}}/>
            {history.map(button)}
        </Stack>
    );
}

/** Reports the Markdown on every change, and handles Ctrl/Cmd+Enter (submit) and Escape (cancel). */
function Bridge({onChange, onSubmit, onCancel, autoFocus}: Pick<FieldProps, 'onChange' | 'onSubmit' | 'onCancel' | 'autoFocus'>) {
    const [editor] = useLexicalComposerContext();
    useEffect(() => {
        if (autoFocus) editor.focus();
    }, [editor, autoFocus]);
    useEffect(() => editor.registerUpdateListener(({editorState, dirtyElements, dirtyLeaves}) => {
        if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return;
        editorState.read(() => onChange($convertToMarkdownString(TRANSFORMERS)));
    }), [editor, onChange]);
    useEffect(() => editor.registerCommand(KEY_DOWN_COMMAND, (e: KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && onSubmit) {
            e.preventDefault();
            onSubmit();
            return true;
        }
        if (e.key === 'Escape' && onCancel) {
            onCancel();
            return true;
        }
        return false;
    }, COMMAND_PRIORITY_LOW), [editor, onSubmit, onCancel]);
    return null;
}

type FieldProps = {
    /** The starting text. The field is uncontrolled: give it a new `key` to reset it. */
    initialMarkdown?: string;
    onChange: (markdown: string) => void;
    ariaLabel: string;
    placeholder?: string;
    autoFocus?: boolean;
    minHeight?: number;
    /** Ctrl/Cmd+Enter. */
    onSubmit?: () => void;
    /** Escape. */
    onCancel?: () => void;
};

/** A small rich-text field (bold, italic, lists, links, undo/redo) that reads and writes Markdown. */
export const RichTextField: React.FC<FieldProps> = ({initialMarkdown = '', ariaLabel, placeholder, minHeight = 64, ...rest}) => (
    <LexicalComposer initialConfig={{
        namespace: 'rich-text-field',
        nodes: NODES,
        editorState: () => $convertFromMarkdownString(initialMarkdown, TRANSFORMERS),
        onError: e => console.error(e),
        theme: {text: {bold: 'md-bold', italic: 'md-italic'}},
    }}>
        <Box sx={{border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper',
            '&:focus-within': {borderColor: 'primary.main'},
            '& .md-bold': {fontWeight: 700}, '& .md-italic': {fontStyle: 'italic'}}}>
            <Toolbar/>
            <Box sx={{...TEXT_SX, p: 1.5, position: 'relative'}}>
                <RichTextPlugin
                    contentEditable={<ContentEditable aria-label={ariaLabel} aria-multiline
                                                      style={{outline: 'none', minHeight}}/>}
                    placeholder={placeholder ? (
                        <Box sx={{position: 'absolute', top: 12, left: 12, color: 'text.disabled', pointerEvents: 'none'}}>
                            {placeholder}
                        </Box>
                    ) : null}
                    ErrorBoundary={LexicalErrorBoundary}
                />
            </Box>
            <HistoryPlugin/>
            <ListPlugin/>
            <LinkPlugin/>
            <MarkdownShortcutPlugin transformers={TRANSFORMERS}/>
            <Bridge {...rest}/>
        </Box>
    </LexicalComposer>
);

/**
 * Markdown shown as formatted text, matching the editor. Links open in a new tab. Images are left out: an author could
 * otherwise make every viewer's browser fetch an external address.
 */
export const MarkdownText: React.FC<{markdown: string}> = ({markdown}) => (
    <Box sx={TEXT_SX}>
        <ReactMarkdown disallowedElements={['img']}
                       components={{a: ({href, children}) => <a href={href} target="_blank" rel="noreferrer">{children}</a>}}>
            {markdown}
        </ReactMarkdown>
    </Box>
);
